import { AllowancePool } from './hooks/customer-account-allowances';

/**
 * How one allowance pool reads and what colour it carries.
 *
 * Three states, not two. A pool can be **under** its allowance, exactly **full**, or
 * **over** it, and the third is not a theoretical case: nothing enforced these limits on
 * the admin side until 2026-09-10, so accounts above their allowance exist. Account 1 in
 * the local database is one, at two domains against an allowance of one. Painting that as
 * merely full would hide the thing an admin most needs to see.
 */
export type PoolState = 'under' | 'full' | 'over';

export function poolState(pool: AllowancePool|undefined): PoolState {
    if (pool === undefined) {
        return 'under';
    }
    if (pool.used > pool.allowed) {
        return 'over';
    }

    return pool.headroom ? 'under' : 'full';
}

/**
 * `1 of 2 used`, in the order the portal's own count bar says it.
 */
export function poolReading(pool: AllowancePool|undefined): string {
    return `${ pool?.used ?? 0 } of ${ pool?.allowed ?? 0 } used`;
}

/**
 * The token a pool's figure is painted in. Only a pool with nothing left carries a
 * colour: three green numbers side by side say nothing, and the one that matters then
 * has to compete with them.
 */
export function poolInk(pool: AllowancePool|undefined): string|undefined {
    switch (poolState(pool)) {
        case 'over':
            return 'var(--wf-bad)';
        case 'full':
            return 'var(--wf-live-line)';
        default:
            return undefined;
    }
}

/**
 * What to say under the figure, or an empty string when there is nothing worth saying.
 * The sentence names the way out, because raising the plan is the only one.
 */
export function poolNote(pool: AllowancePool|undefined, noun: string): string {
    switch (poolState(pool)) {
        case 'over':
            return `Over the plan by ${ (pool?.used ?? 0) - (pool?.allowed ?? 0) }. Raise the ${ noun } on the Subscription tab.`;
        case 'full':
            return `No ${ noun } left. Raise it on the Subscription tab to add another.`;
        default:
            return '';
    }
}
